import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { UserPlus, X, Eye } from "lucide-react";
import { toast } from "sonner";
import StaffProfile from "@/components/StaffProfile";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;

export default function Staff() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [f, setF] = useState({ email:"", name:"", role:"dsp", password:"", phone:"", hourly_rate: 20, external_staff_id: "" });
  const [params, setParams] = useSearchParams();
  const evvHint = params.get("evv");

  const load = (p = page) => api.get("/users", { params: { page: p, limit: PAGE_SIZE } }).then((r) => {
    setRows(r.data.data); setPage(r.data.page); setPages(r.data.pages); setTotal(r.data.total);
  });
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...f, hourly_rate: Number(f.hourly_rate) });
      toast.success("Staff added");
      setOpen(false); setF({ email:"", name:"", role:"dsp", password:"", phone:"", hourly_rate: 20, external_staff_id: "" });
      load(1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="accp-label">Roster</div>
          <h1 className="text-4xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Staff</h1>
          <p className="text-muted-foreground mt-1">Click any row to open the staff profile.</p>
        </div>
        <button className="accp-btn-primary" onClick={()=>setOpen(true)} data-testid="add-staff-btn">
          <UserPlus size={16}/> Add staff
        </button>
      </div>

      {evvHint && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3"
             data-testid="staff-evv-resolve-banner">
          <div className="flex-1">
            <div className="font-semibold">Resolve EVV Staff ID</div>
            <div className="text-xs mt-1">
              An EVV import referenced <span className="font-mono bg-white/60 px-1 rounded">{evvHint}</span> but no
              staff member is linked to it. Open the matching person's profile below and click
              <span className="font-medium"> Link EVV Staff ID</span> — the value above is ready to paste.
            </div>
          </div>
          <button className="accp-btn-ghost text-xs" onClick={() => setParams({})}
                  data-testid="staff-evv-resolve-dismiss">
            <X size={12}/> Dismiss
          </button>
        </div>
      )}

      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-accent/30 text-left">
            <tr>
              <th className="p-4 accp-label">Code</th>
              <th className="p-4 accp-label">Name</th>
              <th className="p-4 accp-label">Email</th>
              <th className="p-4 accp-label">Role</th>
              <th className="p-4 accp-label">Hourly</th>
              <th className="p-4 accp-label">Status</th>
              <th className="p-4 accp-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-accent/20 cursor-pointer"
                  onClick={()=>setProfileId(u.id)} data-testid={`staff-row-${u.code}`}>
                <td className="p-4 font-mono text-xs text-muted-foreground">{u.code}</td>
                <td className="p-4 font-semibold text-primary">{u.name}</td>
                <td className="p-4 text-muted-foreground">{u.email}</td>
                <td className="p-4"><span className="inline-flex px-2 py-0.5 rounded-full bg-accent text-primary text-xs font-semibold uppercase">{u.role}</span></td>
                <td className="p-4">${(u.hourly_rate||0).toFixed(2)}</td>
                <td className="p-4">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${u.active!==false ? "compliance-valid" : "compliance-expired"}`}>
                    {u.active!==false ? "active" : "inactive"}
                  </span>
                </td>
                <td className="p-4 text-right" onClick={(e)=>e.stopPropagation()}>
                  <button className="accp-btn-ghost text-xs mr-1" onClick={()=>setProfileId(u.id)}>
                    <Eye size={13}/> Profile
                  </button>
                  <button className="accp-btn-ghost text-xs" onClick={()=>toggleActive(u)}>
                    {u.active!==false ? "Deactivate" : "Reactivate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onChange={load} testId="staff-pagination" />
      </div>

      {open && (
        <div className="accp-modal-shell" onClick={()=>setOpen(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit}
                className="accp-modal-card space-y-4" data-testid="staff-form">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Add staff member</h3>
              <button type="button" onClick={()=>setOpen(false)}><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2"><span className="accp-label">Full name</span>
                <input className="accp-input mt-1" required value={f.name} onChange={(e)=>setF({...f, name:e.target.value})}/>
              </label>
              <label className="col-span-2"><span className="accp-label">Email</span>
                <input type="email" className="accp-input mt-1" required value={f.email} onChange={(e)=>setF({...f, email:e.target.value})}/>
              </label>
              <label>
                <span className="accp-label">Role</span>
                <select className="accp-input mt-1" value={f.role} onChange={(e)=>setF({...f, role:e.target.value})}>
                  <option value="dsp">DSP</option><option value="rn">RN</option><option value="admin">Admin</option>
                </select>
              </label>
              <label>
                <span className="accp-label">Hourly rate</span>
                <input type="number" step="0.01" className="accp-input mt-1" value={f.hourly_rate} onChange={(e)=>setF({...f, hourly_rate:e.target.value})}/>
              </label>
              <label className="col-span-2"><span className="accp-label">Phone</span>
                <input className="accp-input mt-1" value={f.phone} onChange={(e)=>setF({...f, phone:e.target.value})}/>
              </label>
              <label className="col-span-2">
                <span className="accp-label">EVV Staff ID <span className="font-normal text-muted-foreground normal-case">(optional)</span></span>
                <input
                  className="accp-input mt-1 font-mono text-xs"
                  placeholder="staff/4fa4c7a7-a0ec-…"
                  value={f.external_staff_id}
                  onChange={(e)=>setF({...f, external_staff_id:e.target.value})}
                  data-testid="staff-form-external-staff-id"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  This field is only required for staff who receive imported EVV
                  visits (e.g. Sandata or other supported EVV providers). Test
                  users and non-EVV staff can safely leave this blank.
                </div>
              </label>
              <label className="col-span-2"><span className="accp-label">Temporary password</span>
                <input className="accp-input mt-1" required minLength={6} value={f.password} onChange={(e)=>setF({...f, password:e.target.value})}/>
              </label>
            </div>
            <button type="submit" className="accp-btn-primary w-full" data-testid="staff-submit">Create account</button>
          </form>
        </div>
      )}

      {profileId && <StaffProfile userId={profileId} onClose={()=>setProfileId(null)} onRefresh={() => load()}/>}
    </div>
  );
}
